import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { HrAccessGuard } from '../../components/hr/HrAccessGuard'
import { Button } from '../../components/primitives/Button'
import { Modal } from '../../components/primitives/Modal'
import { YearRolloverPanel } from '../../components/hr/YearRolloverPanel'
import { DefaultsModal } from '../../components/hr/credits/DefaultsModal'
import { flags } from '../../lib/feature-flags'
import { MatrixRoster } from './MatrixRoster'

/**
 * Entitlement tab — per-employee leave entitlement across every leave type,
 * as a wide roster (same shape as Balances, but each cell is the year's
 * entitlement). Per-row Adjust opens the detail pop-up in edit mode to apply
 * a signed override; the header carries the org-wide credit-admin actions:
 *   • Migrate balances — CSV import wizard (flag hrLeaveBalanceImport)
 *   • Defaults         — the 3×N entitlement-defaults grid (DefaultsModal)
 *   • Annual reset     — Jan-1 rollover (YearRolloverPanel)
 *
 * Replaces the former single-leave-type credits page (tab bar + KPI cards +
 * one-type roster).
 */

export function EntitlementTab(): JSX.Element {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [defaultsOpen, setDefaultsOpen] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)

  const refreshRoster = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['balances-matrix'] })
  }

  // Solid primary buttons, matching the "Grant replacement leave" CTA.
  const actions = (
    <>
      {flags.hrLeaveBalanceImport ? (
        <Button
          variant="primary"
          data-testid="entitlement-migrate"
          title="Set employees' leave entitlements from a CSV"
          onClick={() => void navigate({ to: '/hr/leave-import' })}
        >
          Set entitlements
        </Button>
      ) : null}
      <Button variant="primary" data-testid="entitlement-defaults" onClick={() => setDefaultsOpen(true)}>
        Defaults
      </Button>
      <Button
        variant="primary"
        data-testid="entitlement-annual-reset"
        onClick={() => setResetOpen(true)}
      >
        Annual reset · Jan 1
      </Button>
    </>
  )

  return (
    <HrAccessGuard>
      <MatrixRoster metric="entitled" testIdPrefix="entitlement" actions={actions} />

      <Modal open={resetOpen} onClose={() => setResetOpen(false)} title="Annual reset">
        <YearRolloverPanel />
      </Modal>

      <DefaultsModal
        open={defaultsOpen}
        onClose={() => setDefaultsOpen(false)}
        onSaved={refreshRoster}
      />
    </HrAccessGuard>
  )
}
